import { httpQuery } from '@luminovo/data-stack';
import { searchParam } from '@luminovo/design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { JSX } from 'react';
import { z } from 'zod/v4';

import { singleSubassemblyOf } from '@/areas/assemblies/Assemblies/components/singleSubassembly';
import TopLevelAssembliesPage from '@/areas/assemblies/Assemblies/TopLevelAssembliesPage';
import { assembliesModulePermissions } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// A BOM with a single subassembly opens on it; the loader reads the BOM and its paths with the page's queries.
// `hideTopLevelAssembly` stays on the BOM, for a link that must not land on the subassembly, such as after deleting it.
export const Route = createFileRoute('/_authenticated/next/projects/$projectId/assemblies/')({
    staticData: {
        access: { permissions: ['view:assembly'], modulePermissions: assembliesModulePermissions },
        chrome: 'none',
    } satisfies PageStaticData,
    validateSearch: z.object({ hideTopLevelAssembly: searchParam.boolean('optional') }),
    loaderDeps: ({ search }) => ({ hideTopLevelAssembly: search.hideTopLevelAssembly }),
    loader: async ({ context: { queryClient }, params: { projectId }, deps }) => {
        if (deps.hideTopLevelAssembly) {
            return;
        }
        // The BOM is the assembly whose id is the project's.
        const [
            {
                items: [bom],
            },
            { multi_parent_items: parents },
        ] = await Promise.all([
            queryClient.ensureQueryData(
                httpQuery('POST /assemblies/bulk', {
                    requestBody: { ids: [projectId] },
                    queryParams: { with_aggregations: false },
                }),
            ),
            queryClient.ensureQueryData(
                httpQuery('GET /assemblies/:assemblyId/parents', { pathParams: { assemblyId: projectId } }),
            ),
        ]);
        const subassemblyId = singleSubassemblyOf(bom, parents);
        if (subassemblyId !== undefined) {
            throw redirect({
                to: '/projects/$projectId/bom/assembly/$assemblyId',
                params: { projectId, assemblyId: subassemblyId },
                search: { currentParentAssemblyId: projectId },
                replace: true,
            });
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const pathParams = Route.useParams();
    return <TopLevelAssembliesPage {...pathParams} />;
}
